import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest, concat, of } from 'rxjs';
import { bufferTime, catchError, filter, map, mergeMap, switchMap, tap } from 'rxjs/operators';

import { AiAssistantStreamService } from '@core/api/ai-assistant/ai-assistant-stream.service';
import { AiAssistantApiService } from '@core/api/ai-assistant/ai-assistant.service';
import { ToastService } from '@core/services/toast.service';

import { AiStreamFrame } from '@domain/ai-assistant/ai-assistant-stream.models';
import { SendConversationInputRequest } from '@domain/ai-assistant/ai-assistant.models';
import { setTitleFromUserMessage } from '@domain/ai-assistant/conversation-titles';

import { ToastMessageType } from '@shared/components/toast-message/toast-message.view-model';
import { cancelOnAction } from '@shared/operators/cancel-on-action.operator';

import {
  clearAiAssistant,
  createConversation,
  createConversationFailure,
  createConversationSuccess,
  ensurePromptPresetsLoaded,
  loadConversationMessages,
  loadConversationMessagesFailure,
  loadConversationMessagesSuccess,
  loadConversations,
  loadConversationsFailure,
  loadConversationsSuccess,
  loadNextConversationsPage,
  loadPromptPresets,
  loadPromptPresetsFailure,
  loadPromptPresetsSuccess,
  loadRecentConversations,
  loadRecentConversationsFailure,
  loadRecentConversationsSuccess,
  openPanel,
  resetAiAssistant,
  selectConversation,
  sendConsentDecision,
  sendMessage,
  startNewConversation,
  stopStream,
  streamErrored,
  streamStarted,
} from './ai-assistant.actions';
import {
  selectConversations,
  selectConversationsHasMore,
  selectConversationsLoading,
  selectPromptPresets,
  selectPromptPresetsLoading,
} from './ai-assistant.selectors';
import { batchFramesToActions } from './helpers/stream-batch-actions';

const STREAM_BUFFER_TIME_MS = 70;
const RECENT_CONVERSATIONS_LIMIT = 3;
const CONVERSATIONS_PAGE_SIZE = 100;

@Injectable()
export class AiAssistantEffects {
  readonly #actions$ = inject(Actions);
  readonly #store = inject(Store);
  readonly #apiService = inject(AiAssistantApiService);
  readonly #streamService = inject(AiAssistantStreamService);
  readonly #toastService = inject(ToastService);
  readonly #translateService = inject(TranslateService);

  // ---------- Panel ----------

  public openSmallPanel$ = createEffect(() =>
    this.#actions$.pipe(
      ofType(openPanel),
      filter(({ size }) => size === 'small'),
      map(() => startNewConversation())
    )
  );

  public openFullscreenPanel$ = createEffect(() =>
    this.#actions$.pipe(
      ofType(openPanel),
      filter(({ size }) => size === 'fullscreen'),
      map(() => loadConversations({}))
    )
  );

  public loadPromptPresetsOnPanelOpen$ = createEffect(() =>
    this.#actions$.pipe(
      ofType(openPanel),
      map(() => ensurePromptPresetsLoaded())
    )
  );

  public ensurePromptPresetsLoaded$ = createEffect(() =>
    this.#actions$.pipe(
      ofType(ensurePromptPresetsLoaded),
      concatLatestFrom(() => combineLatest([this.#store.select(selectPromptPresets), this.#store.select(selectPromptPresetsLoading)])),
      filter(([, [promptPresets, isLoading]]) => promptPresets === undefined && !isLoading),
      map(() => loadPromptPresets())
    )
  );

  public loadPromptPresets$ = createEffect(() =>
    this.#actions$.pipe(
      ofType(loadPromptPresets),
      switchMap(() =>
        this.#apiService.getPromptPresets$().pipe(
          map((promptPresets) => loadPromptPresetsSuccess({ promptPresets })),
          catchError((error: HttpErrorResponse) =>
            of(loadPromptPresetsFailure({ error: error.message ?? 'Failed to load prompt presets' }))
          )
        )
      )
    )
  );

  // ---------- Conversations ----------

  public createConversation$ = createEffect(() =>
    this.#actions$.pipe(
      ofType(createConversation),
      switchMap(({ pendingMessage }) =>
        this.#apiService.createConversation$().pipe(
          map((response) => createConversationSuccess({ conversationId: response.id, pendingMessage })),
          catchError((error: HttpErrorResponse) =>
            of(createConversationFailure({ error: error.message ?? 'Failed to create conversation' }))
          )
        )
      )
    )
  );

  public createConversationSuccessLoadConversations$ = createEffect(() =>
    this.#actions$.pipe(
      ofType(createConversationSuccess),
      map(() => loadConversations({}))
    )
  );

  public createConversationSuccessLoadRecentConversations$ = createEffect(() =>
    this.#actions$.pipe(
      ofType(createConversationSuccess),
      map(() => loadRecentConversations())
    )
  );

  public loadRecentConversations$ = createEffect(() =>
    this.#actions$.pipe(
      ofType(loadRecentConversations),
      switchMap(() =>
        this.#apiService.getConversations$(RECENT_CONVERSATIONS_LIMIT, 0).pipe(
          map((response) => loadRecentConversationsSuccess({ conversations: response.conversations })),
          catchError((error: HttpErrorResponse) =>
            of(loadRecentConversationsFailure({ error: error.message ?? 'Failed to load recent conversations' }))
          )
        )
      )
    )
  );

  public sendPendingMessageAfterCreate$ = createEffect(() =>
    this.#actions$.pipe(
      ofType(createConversationSuccess),
      filter(({ pendingMessage }) => !!pendingMessage),
      map(({ conversationId, pendingMessage }) => sendMessage({ conversationId, message: pendingMessage! }))
    )
  );

  public loadConversations$ = createEffect(() =>
    this.#actions$.pipe(
      ofType(loadConversations),
      switchMap(({ offset }) =>
        this.#apiService.getConversations$(CONVERSATIONS_PAGE_SIZE, offset ?? 0).pipe(
          map((response) =>
            loadConversationsSuccess({ conversations: response.conversations, total: response.total, offset: response.offset })
          ),
          catchError((error: HttpErrorResponse) => of(loadConversationsFailure({ error: error.message ?? 'Failed to load conversations' })))
        )
      )
    )
  );

  public loadNextConversationsPage$ = createEffect(() =>
    this.#actions$.pipe(
      ofType(loadNextConversationsPage),
      concatLatestFrom(() =>
        combineLatest([
          this.#store.select(selectConversations),
          this.#store.select(selectConversationsHasMore),
          this.#store.select(selectConversationsLoading),
        ])
      ),
      filter(([, [, hasMore, isLoading]]) => hasMore && !isLoading),
      map(([, [conversations]]) => loadConversations({ offset: conversations.length }))
    )
  );

  public selectConversationLoadMessages$ = createEffect(() =>
    this.#actions$.pipe(
      ofType(selectConversation),
      map(({ conversationId }) => loadConversationMessages({ conversationId }))
    )
  );

  public loadConversationMessages$ = createEffect(() =>
    this.#actions$.pipe(
      ofType(loadConversationMessages),
      switchMap(({ conversationId }) =>
        this.#apiService.getConversationMessages$(conversationId).pipe(
          map((response) =>
            loadConversationMessagesSuccess({
              conversationId,
              status: response.status,
              messages: response.messages,
            })
          ),
          catchError((error: HttpErrorResponse) =>
            of(loadConversationMessagesFailure({ error: error.message ?? 'Failed to load messages' }))
          )
        )
      )
    )
  );

  // ---------- Streaming ----------

  public sendMessage$ = createEffect(() =>
    this.#actions$.pipe(
      ofType(sendMessage),
      tap(({ conversationId, message }) => setTitleFromUserMessage(conversationId, message)),
      switchMap(({ conversationId, message }) =>
        concat(of(streamStarted({ conversationId })), this.#streamFrames$(conversationId, { type: 'USER_MESSAGE', message }))
      )
    )
  );

  public sendConsentDecision$ = createEffect(() =>
    this.#actions$.pipe(
      ofType(sendConsentDecision),
      switchMap(({ conversationId, consentId, decision }) =>
        concat(
          of(streamStarted({ conversationId })),
          this.#streamFrames$(conversationId, { type: 'TOOL_CONSENT_DECISION', consentId, decision })
        )
      )
    )
  );

  // ---------- Error toasts ----------

  public showStreamErrorToast$ = createEffect(
    () =>
      this.#actions$.pipe(
        ofType(streamErrored),
        tap(() => {
          this.#toastService.openToast({
            type: ToastMessageType.Error,
            message$: this.#translateService.get('ai-assistant.error.stream-failed'),
            closeable: true,
            duration: 5000,
          });
        })
      ),
    { dispatch: false }
  );

  public showCreateConversationErrorToast$ = createEffect(
    () =>
      this.#actions$.pipe(
        ofType(createConversationFailure),
        tap(() => {
          this.#toastService.openToast({
            type: ToastMessageType.Error,
            message$: this.#translateService.get('ai-assistant.error.create-conversation-failed'),
            closeable: true,
            duration: 5000,
          });
        })
      ),
    { dispatch: false }
  );

  // ---------------------------------------------------------------------------
  // Private: SSE → batched NgRx actions
  // ---------------------------------------------------------------------------

  #streamFrames$(conversationId: string, body: SendConversationInputRequest) {
    return this.#streamService.streamConversationInput$(conversationId, body).pipe(
      bufferTime(STREAM_BUFFER_TIME_MS),
      filter((frames): frames is AiStreamFrame[] => frames.length > 0),
      mergeMap((frames) => batchFramesToActions(conversationId, frames)),
      catchError((error: Error) => of(streamErrored({ conversationId, message: error.message ?? 'Stream connection error' }))),
      cancelOnAction(this.#actions$, [stopStream, clearAiAssistant, resetAiAssistant])
    );
  }
}
